/**
 * I tuoi domini: rivendicali, provali, creaci una casella.
 *
 * La schermata ha un solo compito difficile: far mettere un record TXT a
 * qualcuno che non sa cosa sia. Quindi niente gergo prima del necessario, il
 * valore da copiare in evidenza con un bottone che lo copia davvero, e una
 * frase che dica cosa succede dopo.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Globe, Plus, Check, Copy, Loader2, AlertCircle, Trash2, Mail, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { domainsApi, accountsApi, type OwnedDomain } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

export function DomainsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [nuovo, setNuovo] = useState('');
  const [apertoId, setApertoId] = useState<string | null>(null);
  const [local, setLocal] = useState('');
  const [password, setPassword] = useState('');

  const q = useQuery({ queryKey: ['domains'], queryFn: () => domainsApi.list() });
  const domini = q.data?.domains ?? [];
  const creazioneAttiva = q.data?.mailboxCreation ?? false;

  const aggiungi = useMutation({
    mutationFn: () => domainsApi.add(nuovo.trim()),
    onSuccess: (r) => {
      setNuovo('');
      setApertoId(r.domain.id);
      qc.invalidateQueries({ queryKey: ['domains'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco ad aggiungere il dominio'),
  });

  const verifica = useMutation({
    mutationFn: (id: string) => domainsApi.verify(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['domains'] });
      if (r.domain.verified) {
        toast.success(
          r.domain.hostedByUs
            ? 'Dominio verificato. Puoi creare qui le tue caselle.'
            : 'Dominio verificato.'
        );
      } else {
        toast.error(r.domain.lastError ?? 'Record non trovato');
      }
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Verifica non riuscita'),
  });

  const elimina = useMutation({
    mutationFn: (id: string) => domainsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['domains'] }),
  });

  /** Crea la casella e la collega: due passi che per chi guarda sono uno solo. */
  const creaCasella = useMutation({
    mutationFn: async (d: OwnedDomain) => {
      const { email } = await domainsApi.createMailbox(d.id, local.trim(), password);
      await accountsApi.add({ emailAddress: email, password });
      return email;
    },
    onSuccess: (email) => {
      setLocal(''); setPassword(''); setApertoId(null);
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['billing-me'] });
      toast.success(`${email} è pronta: la trovi nella posta.`);
      navigate('/');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Creazione non riuscita'),
  });

  async function copia(testo: string) {
    try {
      await navigator.clipboard.writeText(testo);
      toast.success('Copiato');
    } catch {
      toast.error('Copia non riuscita: selezionalo a mano');
    }
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-8 space-y-7">

          <header className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
              <Globe className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">I tuoi domini</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Dimostra che un dominio è tuo: da lì puoi creare le caselle che ti servono.
              </p>
            </div>
          </header>

          <form
            className="flex gap-2"
            onSubmit={(e) => { e.preventDefault(); if (nuovo.trim()) aggiungi.mutate(); }}
          >
            <Input
              placeholder="azienda.it"
              value={nuovo}
              onChange={(e) => setNuovo(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={!nuovo.trim() || aggiungi.isPending}>
              {aggiungi.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Aggiungi
            </Button>
          </form>

          {q.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Carico…
            </div>
          )}

          {!q.isLoading && domini.length === 0 && (
            <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              Nessun dominio ancora. Aggiungi il dominio della tua azienda per creare le caselle da qui,
              invece di chiederle a qualcuno.
            </div>
          )}

          <div className="space-y-3">
            {domini.map((d) => (
              <div key={d.id} className={cn(
                'rounded-xl border p-5 space-y-4',
                d.verified && 'border-emerald-300 bg-emerald-50/30'
              )}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                      {d.domain}
                      {d.verified && (
                        <span className="text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" /> Verificato
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {!d.verified
                        ? 'In attesa del record TXT'
                        : d.hostedByUs
                          ? 'Ospitato da noi: puoi creare le caselle qui'
                          : d.hostedByUs === false
                            ? 'Ospitato altrove: collega le caselle che hai già'
                            : 'Verificato'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => elimina.mutate(d.id)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={`Rimuovi ${d.domain}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {!d.verified && (
                  <div className="space-y-3">
                    <p className="text-sm">
                      Aggiungi questo record nel pannello DNS del dominio. È la prova che il dominio è tuo:
                      può metterlo solo chi lo governa.
                    </p>
                    <div className="rounded-lg border bg-muted/40 p-3 space-y-2 text-sm">
                      <Riga etichetta="Tipo" valore="TXT" />
                      <Riga etichetta="Nome" valore={d.txtRecord.name} onCopia={() => copia(d.txtRecord.name)} />
                      <Riga etichetta="Valore" valore={d.txtRecord.value} onCopia={() => copia(d.txtRecord.value)} />
                    </div>
                    {d.lastError && (
                      <div className="text-sm rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 flex gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{d.lastError}</span>
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={() => verifica.mutate(d.id)}
                      disabled={verifica.isPending}
                    >
                      {verifica.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      Ho aggiunto il record
                    </Button>
                  </div>
                )}

                {d.verified && d.hostedByUs && creazioneAttiva && (
                  apertoId === d.id ? (
                    <form
                      className="space-y-3 border-t pt-4"
                      onSubmit={(e) => { e.preventDefault(); creaCasella.mutate(d); }}
                    >
                      <div className="space-y-1">
                        <Label htmlFor={`local-${d.id}`}>Nuova casella</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            id={`local-${d.id}`}
                            placeholder="info"
                            value={local}
                            onChange={(e) => setLocal(e.target.value)}
                            className="flex-1"
                            autoFocus
                          />
                          <span className="text-sm text-muted-foreground shrink-0">@{d.domain}</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`pwd-${d.id}`}>Password della casella</Label>
                        <Input
                          id={`pwd-${d.id}`}
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="almeno 10 caratteri"
                        />
                        <p className="text-xs text-muted-foreground">
                          Serve anche per leggere la posta da telefono o da un altro programma. Annotala.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button" variant="outline" size="sm" className="flex-1"
                          onClick={() => { setApertoId(null); setLocal(''); setPassword(''); }}
                        >
                          Annulla
                        </Button>
                        <Button
                          type="submit" size="sm" className="flex-1"
                          disabled={!local.trim() || password.length < 10 || creaCasella.isPending}
                        >
                          {creaCasella.isPending
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Creo e collego…</>
                            : 'Crea e collega'}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setApertoId(d.id)}>
                      <Mail className="h-4 w-4" /> Crea una casella
                    </Button>
                  )
                )}

                {d.verified && d.hostedByUs === false && (
                  <Button variant="outline" size="sm" onClick={() => navigate('/setup')}>
                    <Mail className="h-4 w-4" /> Collega una casella esistente
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Riga({ etichetta, valore, onCopia }: { etichetta: string; valore: string; onCopia?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground shrink-0 w-16">{etichetta}</span>
      <code className="font-mono text-xs break-all flex-1 min-w-0">{valore}</code>
      {onCopia && (
        <button type="button" onClick={onCopia} className="text-muted-foreground hover:text-foreground shrink-0" aria-label={`Copia ${etichetta}`}>
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
